import React, { useEffect, useState } from 'react';
import Layout from '../components/layout/Layout';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import PageHeader from '../components/PageHeader';

const OutletDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [outlet, setOutlet] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOutletDetails = async () => {
      try {
        const res = await api.getRestaurantById(id);
        setOutlet(res?.data);
      } catch (err) {
        console.error('❌ Failed to fetch outlet:', err);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchOutletDetails();
  }, [id]);

  if (loading)
    return <div style={styles.loading}>Loading outlet details...</div>;

  if (!outlet)
    return (
      <div style={styles.container}>
        <p style={styles.error}>❌ Outlet not found</p>
        <button onClick={() => navigate('/outlets')} style={styles.backButton}>
          ← Back to Outlets
        </button>
      </div>
    );

  const {
    name,
    description,
    phone,
    cuisineType,
    address,
    imageUrl,
    isOpen,
    isPopular,
    topRated,
    coordinates,
    openingHours = {},
    createdAt,
    updatedAt,
    user,
    admin,
    menuItems = [],
  } = outlet;

  return (
    <Layout>
      <div style={styles.container}>
        <button onClick={() => navigate('/outlets')} style={styles.backButton}>
          ← Back to Outlets
        </button>

        <PageHeader
          eyebrow="Outlet Details"
          title={name}
          subtitle={address || 'Restaurant outlet profile'}
          action={
            <button onClick={() => navigate('/outlets')} style={styles.backButton}>
              Back to Outlets
            </button>
          }
        />

        {imageUrl && (
          <div style={styles.imageContainer}>
            <img src={imageUrl} alt={name} style={styles.image} />
          </div>
        )}

        <div style={styles.section}>
          <h3>📝 Overview</h3>
          <p>
            <strong>Description:</strong> {description || 'N/A'}
          </p>
          <p>
            <strong>Phone:</strong> {phone || 'N/A'}
          </p>
          <p>
            <strong>Address:</strong> {address || 'N/A'}
          </p>
          <p>
            <strong>Cuisine Type:</strong> {cuisineType || 'N/A'}
          </p>
          <p>
            <strong>Status:</strong>{' '}
            <span style={{ color: isOpen ? 'green' : 'red' }}>
              {isOpen ? 'Open' : 'Closed'}
            </span>
          </p>
          <p>
            <strong>Popular:</strong> {isPopular ? 'Yes' : 'No'}
          </p>
          <p>
            <strong>Top Rated:</strong> {topRated ? 'Yes' : 'No'}
          </p>
        </div>

        <div style={styles.section}>
          <h3>📍 Coordinates</h3>
          <p>Latitude: {coordinates?.latitude ?? 'N/A'}</p>
          <p>Longitude: {coordinates?.longitude ?? 'N/A'}</p>
        </div>

        <div style={styles.section}>
          <h3>🕒 Opening Hours</h3>
          {Object.entries(openingHours).map(([day, times]) => (
            <p key={day}>
              <strong>{day.charAt(0).toUpperCase() + day.slice(1)}:</strong>{' '}
              {times.open} - {times.close}
            </p>
          ))}
        </div>

        <div style={styles.section}>
          <h3>👤 Owner</h3>
          {user ? (
            <>
              <p>
                {user.firstName} {user.lastName}
              </p>
              <p>Email: {user.email}</p>
            </>
          ) : (
            <p>No owner assigned</p>
          )}
        </div>

        <div style={styles.section}>
          <h3>🛡️ Assigned Admin</h3>
          {admin ? (
            <>
              <p>
                {admin.firstName} {admin.lastName}
              </p>
              <p>Email: {admin.email}</p>
            </>
          ) : (
            <p>No admin assigned</p>
          )}
        </div>

        <div style={styles.section}>
          <h3>📋 Menu Items</h3>
          {menuItems.length > 0 ? (
            <ul style={styles.menuList}>
              {menuItems.map((item) => (
                <li key={item.id} style={styles.menuItem}>
                  <strong>{item.name}</strong> – {item.description} – R
                  {(item.price / 100).toFixed(2)}
                </li>
              ))}
            </ul>
          ) : (
            <p>No menu items yet</p>
          )}
        </div>

        <div style={styles.section}>
          <p>
            <strong>Created:</strong> {new Date(createdAt).toLocaleString()}
          </p>
          <p>
            <strong>Updated:</strong> {new Date(updatedAt).toLocaleString()}
          </p>
        </div>
      </div>
    </Layout>
  );
};

const styles = {
  container: {
    maxWidth: '1000px',
    margin: '0 auto',
  },
  backButton: {
    padding: '8px 16px',
    fontSize: '14px',
    cursor: 'pointer',
    backgroundColor: '#C9A14A',
    color: '#0F3D2E',
    border: 'none',
    borderRadius: 8,
    fontWeight: 900,
  },
  title: {
    marginBottom: '20px',
    color: '#0F3D2E',
    borderBottom: '2px solid #E6D8BE',
    paddingBottom: '10px',
  },
  section: {
    marginBottom: '30px',
    padding: '20px',
    backgroundColor: '#FFFDF7',
    borderRadius: 8,
    border: '1px solid #E6D8BE',
    boxShadow: '0 8px 24px rgba(15,61,46,0.08)',
    color: '#0F3D2E',
  },
  imageContainer: {
    textAlign: 'center',
    marginBottom: '20px',
  },
  image: {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: 8,
    border: '1px solid #E6D8BE',
  },
  menuList: {
    listStyleType: 'disc',
    paddingLeft: '20px',
  },
  menuItem: {
    marginBottom: '8px',
  },
  loading: {
    padding: '40px',
    textAlign: 'center',
    fontSize: '18px',
    color: '#746A5B',
  },
  error: {
    color: '#D9534F',
    fontWeight: 'bold',
  },
};

export default OutletDetails;
